/**
 * Inbox Hooks - React Query Adapter
 *
 * Instagram-style inbox grouped by sender (recommendation + list_shared).
 *
 * Features:
 * - useInboxThreads(): Infinite query for threads (people list)
 * - useInboxThread(fromUserId): Infinite query for thread items
 * - useInboxUnreadCount(): Query for inbox unread count (separate from notifications)
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { queryKeys } from '../lib/queryKeys';
import { apiFetch } from '../lib/api';

// ============================================================================
// Types (exported for use in components)
// ============================================================================

export interface InboxUser {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
}

export interface InboxThread {
  from_user_id: string;
  last_notification_id: string;
  last_created_at: string;
  last_type: 'recommendation' | 'list_shared';
  preview_text: string;
  unread_count: number;
  from_user: InboxUser;
}

export interface InboxRecommendation {
  media_title: string | null;
  media_poster: string | null;
  media_year: number | null;
  message: string | null;
  is_dismissed?: boolean | null;  // For feedback UI - "Not interested" state
}

export interface InboxWatchlist {
  id: string;
  name: string | null;
  slug: string | null;
  cover_url: string | null;
  item_count: number | null;
}

export interface InboxItem {
  id: string;
  type: 'recommendation' | 'list_shared';
  body: string | null;
  title: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  from_user_id: string;
  recommendation_id: string | null;
  watchlist_id: string | null;
  media_id: number | null;
  media_type: 'movie' | 'tv' | null;
  recommendation: InboxRecommendation | null;
  watchlist: InboxWatchlist | null;
}

interface InboxThreadsResponse {
  threads: InboxThread[];
  total_unread: number;
  next_cursor: string | null;
  has_more: boolean;
}

interface InboxThreadResponse {
  items: InboxItem[];
  next_cursor: string | null;
  has_more: boolean;
}

// ============================================================================
// useInboxUnreadCount - Query hook for inbox unread count
// ============================================================================

interface UseInboxUnreadCountResult {
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to get the inbox unread count (recommendation + list_shared only).
 * Separate from notifications bell.
 */
export function useInboxUnreadCount(): UseInboxUnreadCountResult {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;

  const query = useQuery({
    queryKey: queryKeys.inbox.unreadCount(userId || ''),
    queryFn: async () => {
      if (!token) return 0;

      const data = await apiFetch<{ unread_count?: number }>('/inbox/unread-count', {
        token,
      });
      return data.unread_count || 0;
    },
    enabled: !!userId && !!token,
    staleTime: 60_000, // 60 seconds
  });

  return {
    unreadCount: query.data ?? 0,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}

// ============================================================================
// useInboxThreads - Infinite query for threads (people list)
// ============================================================================

interface UseInboxThreadsOptions {
  limit?: number;
}

interface UseInboxThreadsResult {
  threads: InboxThread[];
  totalUnread: number;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  error: Error | null;
  fetchNextPage: () => void;
  refetch: () => void;
}

/**
 * Hook for infinite scrolling inbox threads.
 * Threads are grouped by sender with preview text and unread count.
 */
export function useInboxThreads(options: UseInboxThreadsOptions = {}): UseInboxThreadsResult {
  const { limit = 20 } = options;
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;

  const query = useInfiniteQuery({
    queryKey: queryKeys.inbox.threads(userId || '', { cursor: null, limit }),
    queryFn: async ({ pageParam }) => {
      if (!token) return { threads: [], total_unread: 0, next_cursor: null, has_more: false };

      const params = new URLSearchParams({ limit: String(limit) });
      if (pageParam) params.set('cursor', pageParam);

      const data = await apiFetch<InboxThreadsResponse>(`/inbox/threads?${params}`, {
        token,
      });
      return data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => (lastPage.has_more ? lastPage.next_cursor : undefined),
    enabled: !!userId && !!token,
    staleTime: 30_000,
  });

  // Flatten all pages into a single threads array
  const threads = query.data?.pages.flatMap((page) => page.threads) ?? [];
  // Total unread from first page (most accurate)
  const totalUnread = query.data?.pages[0]?.total_unread ?? 0;

  return {
    threads,
    totalUnread,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    error: query.error as Error | null,
    fetchNextPage: () => query.fetchNextPage(),
    refetch: () => query.refetch(),
  };
}

// ============================================================================
// useInboxThread - Infinite query for thread items (specific sender)
// ============================================================================

interface UseInboxThreadOptions {
  fromUserId: string;
  limit?: number;
}

interface UseInboxThreadResult {
  items: InboxItem[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  error: Error | null;
  fetchNextPage: () => void;
  refetch: () => void;
}

/**
 * Hook for infinite scrolling inbox thread items.
 * Shows all inbox items from a specific sender.
 */
export function useInboxThread(options: UseInboxThreadOptions): UseInboxThreadResult {
  const { fromUserId, limit = 30 } = options;
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;

  const query = useInfiniteQuery({
    queryKey: queryKeys.inbox.thread(userId || '', fromUserId, { cursor: null }),
    queryFn: async ({ pageParam }) => {
      if (!token) return { items: [], next_cursor: null, has_more: false };

      const params = new URLSearchParams({ limit: String(limit) });
      if (pageParam) params.set('cursor', pageParam);

      const data = await apiFetch<InboxThreadResponse>(`/inbox/thread/${fromUserId}?${params}`, {
        token,
      });
      return data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => (lastPage.has_more ? lastPage.next_cursor : undefined),
    enabled: !!userId && !!token && !!fromUserId,
    staleTime: 30_000,
  });

  // Flatten all pages into a single items array
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  return {
    items,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    error: query.error as Error | null,
    fetchNextPage: () => query.fetchNextPage(),
    refetch: () => query.refetch(),
  };
}

// ============================================================================
// useInboxThreadsPreview - Simple query for dropdown preview (non-infinite)
// ============================================================================

interface UseInboxThreadsPreviewOptions {
  limit?: number;
}

interface UseInboxThreadsPreviewResult {
  threads: InboxThread[];
  totalUnread: number;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook for fetching a small preview of inbox threads for the notification dropdown.
 * Unlike useInboxThreads, this is a simple useQuery (not infinite scroll).
 * Designed for quick display in dropdowns.
 */
export function useInboxThreadsPreview(
  options: UseInboxThreadsPreviewOptions = {}
): UseInboxThreadsPreviewResult {
  const { limit = 5 } = options;
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;

  const query = useQuery({
    queryKey: queryKeys.inbox.threadsPreview(userId || '', { limit }),
    queryFn: async () => {
      if (!token) return { threads: [], total_unread: 0, next_cursor: null, has_more: false };

      const params = new URLSearchParams({ limit: String(limit) });
      const data = await apiFetch<InboxThreadsResponse>(`/inbox/threads?${params}`, {
        token,
      });
      return data;
    },
    enabled: !!userId && !!token,
    staleTime: 30_000, // 30 seconds
  });

  return {
    threads: query.data?.threads ?? [],
    totalUnread: query.data?.total_unread ?? 0,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}

// ============================================================================
// useInboxConversation - Query for conversation view (both sides)
// ============================================================================

export interface ConversationItem extends InboxItem {
  to_user_id: string;
  direction: 'incoming' | 'outgoing';
}

interface InboxConversationResponse {
  items: ConversationItem[];
  next_cursor: string | null;
  has_more: boolean;
}

interface UseInboxConversationOptions {
  otherUserId: string;
  limit?: number;
}

interface UseInboxConversationResult {
  items: ConversationItem[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  error: Error | null;
  fetchNextPage: () => void;
  refetch: () => void;
}

/**
 * Hook for fetching a conversation with another user.
 * Returns BOTH sent and received items, ordered oldest-first (newest at bottom).
 */
export function useInboxConversation(options: UseInboxConversationOptions): UseInboxConversationResult {
  const { otherUserId, limit = 50 } = options;
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;

  const query = useInfiniteQuery({
    queryKey: ['inbox', userId, 'conversation', otherUserId, { cursor: null }],
    queryFn: async ({ pageParam }) => {
      if (!token) return { items: [], next_cursor: null, has_more: false };

      const params = new URLSearchParams({ limit: String(limit) });
      if (pageParam) params.set('cursor', pageParam);

      const data = await apiFetch<InboxConversationResponse>(`/inbox/conversation/${otherUserId}?${params}`, {
        token,
      });
      return data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => (lastPage.has_more ? lastPage.next_cursor : undefined),
    enabled: !!userId && !!token && !!otherUserId,
    staleTime: 30_000,
  });

  // Flatten all pages into a single items array
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  return {
    items,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    error: query.error as Error | null,
    fetchNextPage: () => query.fetchNextPage(),
    refetch: () => query.refetch(),
  };
}

// ============================================================================
// useMarkInboxThreadRead - Mutation to mark all items in a thread as read
// ============================================================================

interface MarkThreadReadResponse {
  success: boolean;
  marked_count: number;
}

interface UseMarkInboxThreadReadOptions {
  onSuccess?: (data: MarkThreadReadResponse) => void;
}

/**
 * Mutation hook to mark all inbox items from a sender as read.
 * Automatically invalidates related queries on success.
 */
export function useMarkInboxThreadRead(options: UseMarkInboxThreadReadOptions = {}) {
  const { session, user } = useAuth();
  const token = session?.access_token;
  const userId = user?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fromUserId: string) => {
      if (!token) throw new Error('Not authenticated');

      return apiFetch<MarkThreadReadResponse>(`/inbox/thread/${fromUserId}/read`, {
        method: 'POST',
        token,
      });
    },
    onSuccess: (data, fromUserId) => {
      // Invalidate related queries to reflect the read state
      if (userId) {
        // Invalidate the specific thread
        queryClient.invalidateQueries({
          queryKey: queryKeys.inbox.threadBase(userId, fromUserId),
        });
        // Invalidate threads list (updates unread count)
        queryClient.invalidateQueries({
          queryKey: queryKeys.inbox.threadsBase(userId),
        });
        // Invalidate threads preview
        queryClient.invalidateQueries({
          queryKey: queryKeys.inbox.threadsPreview(userId, { limit: 5 }),
        });
        // Invalidate unread count
        queryClient.invalidateQueries({
          queryKey: queryKeys.inbox.unreadCount(userId),
        });
      }
      options.onSuccess?.(data);
    },
  });
}
